import {
  findCurrUser,
  getUserAllTenants,
  getUserApplications,
  getAllResources,
  fetchSystemSwitch,
  getUserRoleResources,
  setTenant,
} from '@/api/userApi.js';
import config from '@/config/user.env';
import { MdMessageBox, MdMessage } from 'mdesign3';
import { logout, CryptoHelper } from '@/utils/index';
import utils from '@/utils/common';
import cookies from 'js-cookie';
import { USER_INFO, CUR_ROLE, ROLE_LIST, CUR_TENANT } from '@/config/const';
import { fa } from 'mdesign3/es/locale';
import { getDictList } from '@/api/userApi';
import store from '@/store/index';
import { UserRoleHelper } from '@/utils/userRoleHelper';
import { storageResource } from '@/views/login/components/utilsLogin';
import { IStorageResourceParams } from '@/utils/interface';
import { getPrefetchedApplications, getPrefetchedRoleResources, prefetchUserRoleResources } from '@/utils/roleResourcesCache';
import { normalizeUserCode } from '@/utils/userCodeHelper';
import { processRoleResourcesResponse } from '@/utils/processRoleResources';
import { prefetchMicroAppsByAuthority, MICRO_APP_PREFETCH_DELAY_MS } from '@/utils/prefetchMicroAppsByAuthority';

export default {
  state: {
    user: {},
    authority: '',
    accessToken: localStorage.getItem('accessToken') || null,
    appId: config.appId,
    tenantInfo: {},
    currentTenant: {},
    appList: [],
    currentApplicationCode: '',
    btnPermissions: null,
    switchs: [],
    applicationCode: '',
    curRole: '',
    roleList: [],
    maintenancePageData: {
      init: false,
      list: [],
      time: null,
    },
  },
  getters: {
    user: (state) => state.user,
    accessToken: (state) => state.accessToken,
    appId: (state) => state.appId,
    authority: (state) => state.authority,
    menus: (state) => {
      if (state.authority) {
        return state.authority;
      }
      return [];
    },
    curRole: (state) => state.curRole,
    roleList: (state) => state.roleList,
    tenantInfo: (state) => state.tenantInfo,
    currentTenant: (state) => state.currentTenant,
    appList: (state) => state.appList,
    btnPermissions: (state) => state.btnPermissions,
    systemSwitchs: (state) => state.switchs,
    applicationCode: (state) => state.applicationCode,
    maintenancePageData: (state) => state.maintenancePageData,
    // applicationCode: (state) => {
    //   const currentPath = state.appList.find((item) => {
    //     if (item.applicationDomain) {
    //       return item.applicationDomain.indexOf(window.location.pathname) >= 0;
    //     }
    //     return false;
    //   });
    //   return currentPath ? currentPath.applicationCode : '';
    // },
  },
  mutations: {
    getBtnPermissions(state, btnPermissions) {
      state.btnPermissions = btnPermissions;
    },
    getSystemSwitchs(state, switchs) {
      state.switchs = switchs;
    },
    updateCurRole(state, curRole) {
      state.curRole = curRole;
    },
    updateRoleList(state, roleList) {
      state.roleList = roleList;
    },
    updateUserInfo(state, user) {
      state.user = user;
    },
    updateAuthority(state, authority) {
      state.authority = authority;
    },
    updateTenant(state, _tenantInfo) {
      state.tenantInfo = _tenantInfo;
    },
    updateCurrenTenant(state, _tenantInfo) {
      state.currentTenant = _tenantInfo;
    },
    updateAppList(state, _appList) {
      state.appList = _appList;
    },
    updateAccessToken(state, accessToken) {
      state.accessToken = accessToken;
    },
    updateCurrentApplicationCode(state, applicationCode) {
      state.currentApplicationCode = applicationCode;
      state.applicationCode = applicationCode;
      state.userStore.applicationCode = applicationCode;
      state.userStore.currentApplicationCode = applicationCode;
    },
    updateMaintenancePageData(state, maintenancePageData) {
      state.maintenancePageData = maintenancePageData;
    },
  },
  actions: {
    /**
     * 初始化系统
     * @param {*} param0
     */
    async initSystem({ commit, state }) {
      let userInfo = state.user?.userCode ? { ...state.user } : null;

      if (!userInfo) {
        const currUserRes: any = await findCurrUser();
        if (currUserRes && currUserRes?.code !== '0') {
          MdMessageBox.alert(currUserRes.msg || '接口返回异常', '提示', {
            confirmButtonText: '确定',
            callback: () => {
              logout(true);
            },
          });
          return null;
        }
        userInfo = currUserRes.data || {};
      }

      const tenantInfo = localStorage.getItem(CUR_TENANT) ? JSON.parse(localStorage.getItem(CUR_TENANT)!) : {};
      if (tenantInfo && !tenantInfo.id) {
        MdMessageBox.alert('不存在默认租户，请联系管理员重新登录!', '错误', {
          confirmButtonText: '确定',
          callback: () => {
            logout(true);
          },
        });
        return null;
      }

      const curRole = localStorage.getItem(CUR_ROLE) || '';
      const roleList = localStorage.getItem(ROLE_LIST) ? JSON.parse(localStorage.getItem(ROLE_LIST)!) : [];
      const applicationCode = config.applicationCode;

      // 尽早发起角色资源预取（与 setTenant 并行），供 early prefetch 与 initSystem 缓存命中
      if (curRole) {
        prefetchUserRoleResources(userInfo.userCode, curRole);
      }

      await setTenant(tenantInfo.tenantCode);

      const apmUser = cookies.get('apmUser');
      userInfo.apmUser = apmUser;
      commit('updateTenant', {});
      commit('updateCurrenTenant', tenantInfo || {});
      commit('updateCurrentApplicationCode', applicationCode);
      commit('updateCurRole', curRole);
      commit('updateRoleList', roleList);
      if (!state.user?.userCode) {
        const key = await CryptoHelper.generateKey();
        const encryptedData = await CryptoHelper.encrypt(JSON.stringify(userInfo), key);
        localStorage.setItem(USER_INFO, encryptedData);
      }
      localStorage.setItem('applicationCode', JSON.stringify(applicationCode));
      commit('updateUserInfo', userInfo);

      let userCodeOfUserInfo = normalizeUserCode(userInfo.userCode);

      const [appRes, roleRes] = await Promise.all([
        getPrefetchedApplications(userInfo.userCode).then(
          (cached) =>
            cached ||
            getUserApplications({
              userCode: userInfo.userCode,
              tenantCode: 'annto',
            }),
        ),
        getPrefetchedRoleResources(userInfo.userCode, curRole).then(
          (cached) =>
            cached ||
            getUserRoleResources({
              userCode: userCodeOfUserInfo,
              applicationCode,
              tenantCode: 'annto',
              roleCodes: curRole,
            }),
        ),
      ]);

      const appList = appRes?.data || [];
      if (appList.length === 0) {
        MdMessageBox.alert('不存在应用列表，请联系管理员重新登录!', '错误', {
          confirmButtonText: '确定',
          callback: () => {
            logout(true);
          },
        });
        return null;
      }
      commit('updateAppList', appList);

      // checkGrayPermission();
      if (roleRes && +roleRes.code === 0) {
        const { data, authority, btnPermissions } = processRoleResourcesResponse(roleRes);
        commit('getBtnPermissions', btnPermissions);
        commit('updateAuthority', authority);
        if (authority && authority.length === 0 && !location.href.includes('iframeLSP')) {
          return new Promise((resolve) => {
            MdMessageBox.alert('您没有菜单权限，请联系管理员重新登录!', '提示', {
              confirmButtonText: '确定',
              callback: () => {
                resolve({ data, authority: [] });
              },
            });
          });
        }
        if (authority?.length) {
          // 此处不预首页子应用：登录跳转/强刷新时与挂载间隔过短，预加载无作用且易抢带宽；
          // 只调度阶段二——首页非 LSP 时约 3500ms 后预其余白名单（LSP 首页则跳过）。
          prefetchMicroAppsByAuthority(authority, MICRO_APP_PREFETCH_DELAY_MS, userInfo.userCode);
        }
        return { data, authority };
      }

      MdMessageBox.alert(roleRes?.msg || '接口返回异常', '提示', {
        confirmButtonText: '确定',
        callback: () => {
          logout(true);
        },
      });
      return null;
    },
  },
};
// 判断该用户是否有试点权限
const checkGrayPermission = async () => {
  try {
    // 1. 先判断是否在灰度路径（/v3/...）
    const isOnGrayPage = location.pathname.startsWith('/v3/');
    if (!isOnGrayPage) return; // 非灰度页无需检查

    // 请求试点开关
    // 获取v3开关试点字典数据
    const dicRes = await getDictList({ dictCodes: 'EL_SWITCH_CODE' });
    let _VUE3_SWITCH = false; // VUE3试点总开关
    let _VUE3_ROLES = []; // VUE3试点角色编码
    if (+dicRes.code === 0 && dicRes.data) {
      const EL_SWITCH_CODE = dicRes.data.EL_SWITCH_CODE || {};
      _VUE3_SWITCH = EL_SWITCH_CODE.VUE3_SWITCH === '1'; // VUE3试点总开关
      // VUE3试点角色编码
      if (EL_SWITCH_CODE.VUE3_ROLES) {
        _VUE3_ROLES = EL_SWITCH_CODE.VUE3_ROLES.split(',');
      }
    }
    // 2. 判断是否试点用户
    // const roleList = store.state.roleList || [];
    // const roleList = store.state.roleList || []; //本地缓存
    // 接口获取角色组
    // const roleGroupData = await UserRoleHelper.resolveUserRoles(store.getters.user.userCode);
    // if (roleGroupData === null || roleGroupData === undefined || roleGroupData.length === 0) {
    //   UserRoleHelper.showNoRoleMessage();
    //   return;
    // }
    // const isGrayUser =
    //   _VUE3_ROLES.length > 0 && roleGroupData.some((ur: any) => ur?.roleCode && _VUE3_ROLES.includes(ur.roleCode));
    // 3. 如果在灰度页，但用户不是灰度用户 或 试点开关关闭 → 需要重定向回原系统链接
    if (!_VUE3_SWITCH) {
      // 解析当前 URL, new URL() 会自动对 URL 中的 query 部分进行 decodeURIComponent，但 hash 部分不会被解码。
      // 某些浏览器或特定场景下window.location.href 本身可能已经是部分解码或双重编码的状态。
      // micro-app 基座在内部跳转过程中可能对 URL 进行了编码处理,，导致 currentUrl.hash 拿到的是一个“已编码”的字符串
      // 浏览器或基座再次解析时，只解码一次，于是 %26 变成 &，但如果你的原始参数中还有未编码的 &，就会混乱; 或者更常见的是：某些中间件/基座只取第一个 query 参数，后面全丢。
      // 解决方案：编码过的 hash（如 #/path?a=1%26b=2）还原成原始形式（#/path?a=1&b=2）,decodeURIComponent(currentUrl.hash);
      const currentUrl = new URL(window.location.href);
      const newPath = currentUrl.pathname.replace(/^\/v3(?=\/|$)/, '') || '/'; // 移除 /v3 前缀，保留原路径和 hash，根路径兜底, 处理 /v3 → /
      let newHash = currentUrl.hash;
      // 处理登录页：尝试从 redirectUrl 恢复目标地址
      if (currentUrl.hash.startsWith('#/login')) {
        // 手动提取 redirectUrl 的完整值（避免 URLSearchParams 拆分 &）
        const match = currentUrl.hash.match(/^[#]\/login\?redirectUrl=(.+)$/);
        if (match) {
          let redirectUrl = match[1]; // 完整值，包含 ? 和 &
          // 替换 microOms/microTms → microOtp（只替换开头路径）
          redirectUrl = redirectUrl.replace(/^\/(microOms|microTms|microDataReport)(\/|$)/, '/microOtp$2');
          newHash = '#' + redirectUrl;
        } else {
          newHash = '#/index'; // 无 redirectUrl，默认跳首页
        }
      } else {
        // 非登录页：直接还原 microOms/microTms → microOtp
        newHash = newHash.replace(/^#\/(microOms|microTms|microDataReport)(\/.*)?$/, '#/microOtp$2');
      }
      // 构建最终重定向 URL
      const redirectUrl = currentUrl.origin + newPath + currentUrl.search + decodeURIComponent(newHash); // ✅ 关键：对 newHash 解码，还原 & 等字符
      // console.log('checkGrayPermission', {
      //   isGrayUser,
      //   _VUE3_SWITCH,
      //   userRolesList: roleGroupData,
      //   redirectUrl,
      // });
      // // 更新用户本地角色列表缓存
      // const params: IStorageResourceParams = {
      //   roleList: roleGroupData,
      // };
      // await storageResource(params);
      location.href = redirectUrl; // 重定向回原系统链接
    } 
  } catch (error) {
    console.error('checkGrayPermission', error);
  }
}
